import {
  DeviceState,
  EnemyState,
  INTERNAL_WIDTH,
  Owner,
  ProjectileFlags,
  ProjectileKind,
  WEAPON_MAX_LEVEL,
} from '../constants';
import { FORMATION_STATS, FormationType } from '../formation/formations';
import type { GameWorld } from '../world/GameWorld';
import { CoreBeamId, DeviceId, NodeLensId } from './definitions';

type CoreBehavior = (world: GameWorld, angle: number) => void;
type LensBehavior = (world: GameWorld, angle: number) => void;
type DeviceBehavior = (world: GameWorld) => void;

const CORE_COOLDOWN = new Float32Array([5, 10, 13, 18]);
const NODE_COOLDOWN = new Float32Array([10, 12, 15, 18]);

// Per-weapon-level scaling, indexed by PlayerState.weaponLevel (0..WEAPON_MAX_LEVEL).
// Per-shot attackId means multi-shot volleys now actually all land, so growth shrinks vs. shared-attackId era.
const LEVEL_DAMAGE_SCALE = new Float32Array([1, 1, 1.05, 1.1, 1.15]);
const LEVEL_COOLDOWN_SCALE = new Float32Array([1, 1, 0.95, 0.9, 0.85]);

const LINK_LASER_RADIUS = 2.25;
const LINK_LASER_LIFE = 15;
const LINK_LASER_DAMAGE = 16;
const LINK_LASER_COOLDOWN = 5;
const LINK_TARGET_NONE = 0;
const LINK_TARGET_X_WEIGHT = 0.18;
const LINK_TARGET_Y_WEIGHT = 2;
const LINK_TARGET_BEHIND_TOLERANCE = 16;

function nodeProjectileFlags(world: GameWorld): ProjectileFlags {
  return world.nodes.formation === FormationType.Spear ? ProjectileFlags.Piercing : ProjectileFlags.None;
}

// --- Pulse (core 0) ---
const PULSE_SPEED = 7.54; // 5.8 の +30%（自機弾速強化）
const PULSE_RADIUS = 2;
const PULSE_LIFE = 74;
const PULSE_BASE_DAMAGE = 4;
const PULSE_OFFSET_L1 = 3;
const PULSE_OFFSET_L2 = 4;
const PULSE_SPREAD_ANGLE = 0.14;

function firePulse(world: GameWorld, angle: number): void {
  const p = world.player;
  const level = p.weaponLevel;
  const damage = PULSE_BASE_DAMAGE * LEVEL_DAMAGE_SCALE[level] * world.playerDamageScale();
  const x = p.x + 12;
  const vx = Math.cos(angle) * PULSE_SPEED;
  const vy = Math.sin(angle) * PULSE_SPEED;
  if (level === 0) {
    world.projectiles.spawn(x, p.y, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  if (level === 1) {
    world.projectiles.spawn(x, p.y - PULSE_OFFSET_L1, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    world.projectiles.spawn(x, p.y + PULSE_OFFSET_L1, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  if (level === 2) {
    world.projectiles.spawn(x, p.y - PULSE_OFFSET_L2, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    world.projectiles.spawn(x, p.y, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    world.projectiles.spawn(x, p.y + PULSE_OFFSET_L2, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  // level 3 & 4: 3 parallel + 2 spread shots; level 4's center shot pierces.
  const centerFlags = level >= WEAPON_MAX_LEVEL ? ProjectileFlags.Piercing : ProjectileFlags.None;
  world.projectiles.spawn(x, p.y - PULSE_OFFSET_L2, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, centerFlags, world.nextAttackId());
  world.projectiles.spawn(x, p.y + PULSE_OFFSET_L2, vx, vy, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
  const spreadVx1 = Math.cos(angle - PULSE_SPREAD_ANGLE) * PULSE_SPEED;
  const spreadVy1 = Math.sin(angle - PULSE_SPREAD_ANGLE) * PULSE_SPEED;
  const spreadVx2 = Math.cos(angle + PULSE_SPREAD_ANGLE) * PULSE_SPEED;
  const spreadVy2 = Math.sin(angle + PULSE_SPREAD_ANGLE) * PULSE_SPEED;
  world.projectiles.spawn(x, p.y, spreadVx1, spreadVy1, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y, spreadVx2, spreadVy2, PULSE_RADIUS, PULSE_LIFE, damage, Owner.Player, ProjectileKind.Pulse, ProjectileFlags.None, world.nextAttackId());
}

// --- Arc (core 1) ---
const ARC_SPEED = 4.5;
const ARC_RADIUS = 2.5;
const ARC_LIFE = 92;
const ARC_BASE_DAMAGE = 5.5;
const ARC_OFFSET_L1 = 4;
const ARC_OFFSET_L3 = 5;
const ARC_L4_SPEED_BONUS = 0.5;

function fireArc(world: GameWorld, angle: number): void {
  const p = world.player;
  const level = p.weaponLevel;
  const damage = ARC_BASE_DAMAGE * LEVEL_DAMAGE_SCALE[level] * world.playerDamageScale();
  const speed = level >= WEAPON_MAX_LEVEL ? ARC_SPEED + ARC_L4_SPEED_BONUS : ARC_SPEED;
  const x = p.x + 12;
  const vx = Math.cos(angle) * speed;
  const vy = Math.sin(angle) * speed;
  if (level === 0) {
    world.projectiles.spawn(x, p.y, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  if (level === 1 || level === 2) {
    world.projectiles.spawn(x, p.y - ARC_OFFSET_L1, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
    world.projectiles.spawn(x, p.y + ARC_OFFSET_L1, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  // level 3 & 4: 3 parallel shots, level 4 additionally faster (handled via `speed` above).
  world.projectiles.spawn(x, p.y - ARC_OFFSET_L3, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y + ARC_OFFSET_L3, vx, vy, ARC_RADIUS, ARC_LIFE, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, world.nextAttackId());
}

// --- Prism (core 2) ---
const PRISM_SPEED = 4.9;
const PRISM_SPEED_L1 = 5.6;
const PRISM_RADIUS = 2.5;
const PRISM_LIFE = 88;
const PRISM_BASE_DAMAGE = 5;
const PRISM_OFFSET = 4;
const PRISM_SPREAD_ANGLE_L3 = 0.12;
const PRISM_SPREAD_ANGLE_L4 = 0.16;

function firePrism(world: GameWorld, angle: number): void {
  const p = world.player;
  const level = p.weaponLevel;
  const damage = PRISM_BASE_DAMAGE * LEVEL_DAMAGE_SCALE[level] * world.playerDamageScale();
  const x = p.x + 12;
  if (level === 0) {
    const vx = Math.cos(angle) * PRISM_SPEED;
    const vy = Math.sin(angle) * PRISM_SPEED;
    world.projectiles.spawn(x, p.y, vx, vy, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  if (level === 1) {
    const vx = Math.cos(angle) * PRISM_SPEED_L1;
    const vy = Math.sin(angle) * PRISM_SPEED_L1;
    world.projectiles.spawn(x, p.y, vx, vy, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  const vx = Math.cos(angle) * PRISM_SPEED;
  const vy = Math.sin(angle) * PRISM_SPEED;
  world.projectiles.spawn(x, p.y - PRISM_OFFSET, vx, vy, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y + PRISM_OFFSET, vx, vy, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
  if (level === 2) return;
  if (level === 3) {
    const spreadVx1 = Math.cos(angle - PRISM_SPREAD_ANGLE_L3) * PRISM_SPEED;
    const spreadVy1 = Math.sin(angle - PRISM_SPREAD_ANGLE_L3) * PRISM_SPEED;
    const spreadVx2 = Math.cos(angle + PRISM_SPREAD_ANGLE_L3) * PRISM_SPEED;
    const spreadVy2 = Math.sin(angle + PRISM_SPREAD_ANGLE_L3) * PRISM_SPEED;
    world.projectiles.spawn(x, p.y, spreadVx1, spreadVy1, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
    world.projectiles.spawn(x, p.y, spreadVx2, spreadVy2, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
    return;
  }
  // level 4: 2 parallel (above) + 3 spread shots at angle, angle±0.16
  const spreadVx1 = Math.cos(angle - PRISM_SPREAD_ANGLE_L4) * PRISM_SPEED;
  const spreadVy1 = Math.sin(angle - PRISM_SPREAD_ANGLE_L4) * PRISM_SPEED;
  const spreadVx2 = Math.cos(angle + PRISM_SPREAD_ANGLE_L4) * PRISM_SPEED;
  const spreadVy2 = Math.sin(angle + PRISM_SPREAD_ANGLE_L4) * PRISM_SPEED;
  world.projectiles.spawn(x, p.y, vx, vy, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y, spreadVx1, spreadVy1, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
  world.projectiles.spawn(x, p.y, spreadVx2, spreadVy2, PRISM_RADIUS, PRISM_LIFE, damage, Owner.Player, ProjectileKind.Prism, ProjectileFlags.None, world.nextAttackId());
}

// --- Lattice (core 3) ---
const LATTICE_SPEED = 2.8;
const LATTICE_RADIUS = 3.5;
const LATTICE_LIFE = 126;
const LATTICE_BASE_DAMAGE = 8.5;
const LATTICE_VY_TILT_PER_PX = 0.012;
const LATTICE_OFFSET_L01 = 5;
const LATTICE_OFFSET_L2 = 6;
const LATTICE_OFFSET_L3 = 8;
const LATTICE_OFFSET_L4_OUTER = 9;
const LATTICE_OFFSET_L4_INNER = 3;

function spawnLatticePacket(world: GameWorld, x: number, y: number, vx: number, vy: number, damage: number, attackId: number): void {
  world.projectiles.spawn(x, y, vx, vy, LATTICE_RADIUS, LATTICE_LIFE, damage, Owner.Player, ProjectileKind.Lattice, ProjectileFlags.Piercing, attackId);
}

function fireLattice(world: GameWorld, angle: number): void {
  const p = world.player;
  const level = p.weaponLevel;
  const damage = LATTICE_BASE_DAMAGE * LEVEL_DAMAGE_SCALE[level] * world.playerDamageScale();
  const x = p.x + 10;
  const vx = Math.cos(angle) * LATTICE_SPEED;
  const vy = Math.sin(angle) * LATTICE_SPEED;
  const attackId = world.nextAttackId();
  if (level <= 1) {
    spawnLatticePacket(world, x, p.y - LATTICE_OFFSET_L01, vx, vy - LATTICE_OFFSET_L01 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    spawnLatticePacket(world, x, p.y + LATTICE_OFFSET_L01, vx, vy + LATTICE_OFFSET_L01 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    return;
  }
  if (level === 2) {
    spawnLatticePacket(world, x, p.y - LATTICE_OFFSET_L2, vx, vy - LATTICE_OFFSET_L2 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    spawnLatticePacket(world, x, p.y, vx, vy, damage, attackId);
    spawnLatticePacket(world, x, p.y + LATTICE_OFFSET_L2, vx, vy + LATTICE_OFFSET_L2 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    return;
  }
  if (level === 3) {
    spawnLatticePacket(world, x, p.y - LATTICE_OFFSET_L3, vx, vy - LATTICE_OFFSET_L3 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    spawnLatticePacket(world, x, p.y, vx, vy, damage, attackId);
    spawnLatticePacket(world, x, p.y + LATTICE_OFFSET_L3, vx, vy + LATTICE_OFFSET_L3 * LATTICE_VY_TILT_PER_PX, damage, attackId);
    return;
  }
  // level 4: 4 packets
  spawnLatticePacket(world, x, p.y - LATTICE_OFFSET_L4_OUTER, vx, vy - LATTICE_OFFSET_L4_OUTER * LATTICE_VY_TILT_PER_PX, damage, attackId);
  spawnLatticePacket(world, x, p.y - LATTICE_OFFSET_L4_INNER, vx, vy - LATTICE_OFFSET_L4_INNER * LATTICE_VY_TILT_PER_PX, damage, attackId);
  spawnLatticePacket(world, x, p.y + LATTICE_OFFSET_L4_INNER, vx, vy + LATTICE_OFFSET_L4_INNER * LATTICE_VY_TILT_PER_PX, damage, attackId);
  spawnLatticePacket(world, x, p.y + LATTICE_OFFSET_L4_OUTER, vx, vy + LATTICE_OFFSET_L4_OUTER * LATTICE_VY_TILT_PER_PX, damage, attackId);
}

const CORE_BEHAVIORS: readonly CoreBehavior[] = Object.freeze([firePulse, fireArc, firePrism, fireLattice]);

function fireFocus(world: GameWorld, angle: number): void {
  const nodes = world.nodes;
  const speed = 5.1;
  const damage = 2.4 * FORMATION_STATS[nodes.formation].damageScale * world.playerDamageScale();
  for (let i = 0; i < 3; i += 1) {
    if (nodes.disabledTicks[i] > 0 || nodes.fireCooldown[i] > 0) continue;
    world.projectiles.spawn(nodes.x[i], nodes.y[i], Math.cos(angle) * speed, Math.sin(angle) * speed, 1.75, 82, damage, Owner.Player, ProjectileKind.Node, nodeProjectileFlags(world), world.nextAttackId(), i);
    nodes.fireCooldown[i] = NODE_COOLDOWN[NodeLensId.Focus] / FORMATION_STATS[nodes.formation].fireRateScale;
    nodes.pulse[i] = 5;
  }
}

function fireFan(world: GameWorld, angle: number): void {
  const nodes = world.nodes;
  const stats = FORMATION_STATS[nodes.formation];
  for (let i = 0; i < 3; i += 1) {
    if (nodes.disabledTicks[i] > 0 || nodes.fireCooldown[i] > 0) continue;
    const spread = (i - 1) * 0.22;
    const shotAngle = angle + spread;
    world.projectiles.spawn(nodes.x[i], nodes.y[i], Math.cos(shotAngle) * 4.7, Math.sin(shotAngle) * 4.7, 1.75, 88, 2.15 * stats.damageScale * world.playerDamageScale(), Owner.Player, ProjectileKind.Node, nodeProjectileFlags(world), world.nextAttackId(), i);
    nodes.fireCooldown[i] = NODE_COOLDOWN[NodeLensId.Fan] / stats.fireRateScale;
    nodes.pulse[i] = 5;
  }
}

function fireMirror(world: GameWorld, angle: number): void {
  const nodes = world.nodes;
  const stats = FORMATION_STATS[nodes.formation];
  for (let i = 0; i < 3; i += 1) {
    if (nodes.disabledTicks[i] > 0 || nodes.fireCooldown[i] > 0) continue;
    world.projectiles.spawn(nodes.x[i], nodes.y[i], Math.cos(angle) * 4.4, Math.sin(angle) * 4.4, 2, 92, 2 * stats.damageScale * world.playerDamageScale(), Owner.Player, ProjectileKind.Node, nodeProjectileFlags(world), world.nextAttackId(), i);
    nodes.fireCooldown[i] = NODE_COOLDOWN[NodeLensId.Mirror] / stats.fireRateScale;
    nodes.pulse[i] = 5;
  }
}

function fireTether(world: GameWorld, angle: number): void {
  const nodes = world.nodes;
  const stats = FORMATION_STATS[nodes.formation];
  if (nodes.tetherCharge >= 1 && nodes.tetherActiveTicks <= 0) {
    nodes.tetherCharge = 0;
    nodes.tetherActiveTicks = 42;
    world.emitSfx(7);
  }
  for (let i = 0; i < 3; i += 1) {
    if (nodes.disabledTicks[i] > 0 || nodes.fireCooldown[i] > 0) continue;
    world.projectiles.spawn(nodes.x[i], nodes.y[i], Math.cos(angle) * 4, Math.sin(angle) * 4, 2, 96, 1.7 * stats.damageScale * world.playerDamageScale(), Owner.Player, ProjectileKind.Node, nodeProjectileFlags(world), world.nextAttackId(), i);
    nodes.fireCooldown[i] = NODE_COOLDOWN[NodeLensId.Tether] / stats.fireRateScale;
    nodes.pulse[i] = 5;
  }
}

const LENS_BEHAVIORS: readonly LensBehavior[] = Object.freeze([fireFocus, fireFan, fireMirror, fireTether]);

function activateRepairField(world: GameWorld): void {
  world.player.deviceActiveTicks = 300;
  world.player.deviceCooldown = 720;
  world.deviceState = DeviceState.Active;
  world.emitSfx(8);
}

function activatePhaseCut(world: GameWorld): void {
  const active = world.projectiles.indices.active;
  for (let i = 0; i < active.length; i += 1) {
    if (active[i] === 0 || world.projectiles.owner[i] !== Owner.Enemy) continue;
    if ((world.projectiles.flags[i] & ProjectileFlags.Unblockable) !== 0) continue;
    world.particles.spawn(world.projectiles.x[i], world.projectiles.y[i], 0, 0, 24, 2, 3, 3);
    world.projectiles.release(i);
  }
  world.bosses.applyDeviceDamage(18, world);
  world.player.deviceActiveTicks = 20;
  world.player.deviceCooldown = 660;
  world.deviceState = DeviceState.Active;
  world.globalFlashTicks = Math.max(world.globalFlashTicks, 8);
  world.emitSfx(9);
}

function activateTimeFold(world: GameWorld): void {
  world.player.deviceActiveTicks = 270;
  world.player.deviceCooldown = 780;
  world.deviceState = DeviceState.Active;
  world.emitSfx(10);
}

function activateOverdrive(world: GameWorld): void {
  world.player.deviceActiveTicks = 330;
  world.player.deviceCooldown = 840;
  world.deviceState = DeviceState.Active;
  world.emitSfx(11);
}

const DEVICE_BEHAVIORS: readonly DeviceBehavior[] = Object.freeze([activateRepairField, activatePhaseCut, activateTimeFold, activateOverdrive]);

export class WeaponSystem {
  firePlayer(world: GameWorld): void {
    const player = world.player;
    if (!player.alive || player.fireCooldown > 0) return;
    if (!player.aimLocked) {
      let angle = 0;
      if (Math.abs(player.vy) > 0.15) angle = Math.max(-0.62, Math.min(0.62, player.vy * 0.24));
      player.aimDirection = angle;
    }
    const linkLaserActive = world.continuousArsenalEnabled && player.weaponLevel >= WEAPON_MAX_LEVEL;
    const rhythmStep = player.weaponRhythmStep;
    if (linkLaserActive) {
      this.fireLinkLaser(world);
    } else {
      const behavior = CORE_BEHAVIORS[player.core] ?? CORE_BEHAVIORS[CoreBeamId.Pulse]!;
      behavior(world, player.aimDirection);
    }
    const overdrive = player.device === DeviceId.Overdrive && player.deviceActiveTicks > 0;
    const baseCooldown = linkLaserActive ? LINK_LASER_COOLDOWN : (CORE_COOLDOWN[player.core] ?? 8);
    const cooldown = baseCooldown * LEVEL_COOLDOWN_SCALE[player.weaponLevel];
    player.fireCooldown = cooldown / (overdrive ? 1.65 : 1);
    const nodeAngle = world.nodes.directionIndex * (Math.PI / 4);
    const lens = LENS_BEHAVIORS[player.lens] ?? LENS_BEHAVIORS[NodeLensId.Focus]!;
    lens(world, nodeAngle);
    if (world.continuousArsenalEnabled) {
      player.weaponRhythmStep = (rhythmStep + 1) & 15;
    }
    world.emitSfx(linkLaserActive ? 5 : 1);
  }

  activateDevice(world: GameWorld): void {
    const behavior = DEVICE_BEHAVIORS[world.player.device] ?? DEVICE_BEHAVIORS[DeviceId.RepairField]!;
    behavior(world);
  }

  updateProjectiles(world: GameWorld): void {
    // HOTPATH-BEGIN
    const pool = world.projectiles;
    const active = pool.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0) continue;
      const kind = pool.kind[i];
      if (
        kind === ProjectileKind.Laser
        && pool.owner[i] === Owner.Player
        && (pool.flags[i] & ProjectileFlags.Segment) !== 0
      ) {
        if (!this.syncLinkLaser(world, i)) pool.release(i);
        continue;
      }
      if (kind === ProjectileKind.Arc && pool.chainDepth[i] === 0 && pool.age[i] >= 18) {
        const speed = Math.sqrt(pool.vx[i] * pool.vx[i] + pool.vy[i] * pool.vy[i]);
        const base = pool.angle[i];
        const attackId = pool.attackId[i];
        const damage = pool.damage[i] * 0.72;
        pool.spawn(pool.x[i], pool.y[i], Math.cos(base - 0.38) * speed, Math.sin(base - 0.38) * speed, pool.radius[i], 62, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, attackId, 0, 1);
        pool.spawn(pool.x[i], pool.y[i], Math.cos(base + 0.38) * speed, Math.sin(base + 0.38) * speed, pool.radius[i], 62, damage, Owner.Player, ProjectileKind.Arc, ProjectileFlags.None, attackId, 0, 1);
        pool.release(i);
        continue;
      }
      if (kind === ProjectileKind.Lattice) {
        pool.y[i] += Math.sin(pool.age[i] * 0.18 + i) * 0.12;
      } else if ((pool.flags[i] & ProjectileFlags.Homing) !== 0 && pool.owner[i] === Owner.Enemy) {
        let targetX = world.player.x;
        let targetY = world.player.y;
        const source = pool.source[i];
        if (kind === ProjectileKind.EnemyNeedle && source >= 1 && source <= 3) {
          targetX = world.nodes.x[source - 1];
          targetY = world.nodes.y[source - 1];
        }
        const dx = targetX - pool.x[i];
        const dy = targetY - pool.y[i];
        const lenSq = dx * dx + dy * dy;
        if (lenSq > 1) {
          const inv = 1 / Math.sqrt(lenSq);
          pool.vx[i] = pool.vx[i] * 0.97 + dx * inv * 0.055;
          pool.vy[i] = pool.vy[i] * 0.97 + dy * inv * 0.055;
        }
      }
    }
    // HOTPATH-END
  }

  private fireLinkLaser(world: GameWorld): void {
    const player = world.player;
    const x = player.x + 12;
    const pool = world.projectiles;
    let index = this.findLinkLaser(world);
    if (index >= 0) {
      pool.life[index] = LINK_LASER_LIFE;
      pool.damage[index] = LINK_LASER_DAMAGE * world.playerDamageScale();
      pool.attackId[index] = world.nextAttackId();
      this.syncLinkLaser(world, index);
      return;
    }
    index = pool.spawn(
      x,
      player.y,
      0,
      0,
      LINK_LASER_RADIUS,
      LINK_LASER_LIFE,
      LINK_LASER_DAMAGE * world.playerDamageScale(),
      Owner.Player,
      ProjectileKind.Laser,
      ProjectileFlags.Segment,
      world.nextAttackId(),
      0,
      0,
      x,
      player.y,
    );
    if (index < 0) return;
    pool.aux0[index] = this.acquireLinkTarget(world);
    this.syncLinkLaser(world, index);
  }

  private findLinkLaser(world: GameWorld): number {
    const pool = world.projectiles;
    const active = pool.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (
        active[i] !== 0
        && pool.owner[i] === Owner.Player
        && pool.kind[i] === ProjectileKind.Laser
        && (pool.flags[i] & ProjectileFlags.Segment) !== 0
      ) return i;
    }
    return -1;
  }

  /**
   * Positive codes address regular enemies, negative codes address boss parts.
   * Keeping the code in ProjectilePool.aux0 avoids an allocation-bearing target
   * object and lets a live beam reacquire after its target dies.
   */
  private acquireLinkTarget(world: GameWorld): number {
    const player = world.player;
    let bestTarget = LINK_TARGET_NONE;
    let bestScore = Number.POSITIVE_INFINITY;
    const enemies = world.enemies;
    const enemyActive = enemies.indices.active;
    for (let i = 0; i < enemyActive.length; i += 1) {
      if (enemyActive[i] === 0 || enemies.state[i] === EnemyState.Dying) continue;
      const dx = enemies.x[i] - player.x;
      if (dx < 0) continue;
      const dy = enemies.y[i] - player.y;
      const score = dx * dx * LINK_TARGET_X_WEIGHT + dy * dy * LINK_TARGET_Y_WEIGHT;
      if (score >= bestScore) continue;
      bestScore = score;
      bestTarget = i + 1;
    }

    if (!world.bosses.active) return bestTarget;
    const parts = world.bossParts;
    const partActive = parts.indices.active;
    for (let i = 0; i < partActive.length; i += 1) {
      if (partActive[i] === 0 || parts.visible[i] === 0 || parts.vulnerable[i] === 0) continue;
      const dx = parts.x[i] - player.x;
      if (dx < 0) continue;
      const dy = parts.y[i] - player.y;
      const score = dx * dx * LINK_TARGET_X_WEIGHT + dy * dy * LINK_TARGET_Y_WEIGHT;
      if (score >= bestScore) continue;
      bestScore = score;
      bestTarget = -(i + 1);
    }
    return bestTarget;
  }

  private syncLinkLaser(world: GameWorld, projectile: number): boolean {
    const pool = world.projectiles;
    let target = Math.trunc(pool.aux0[projectile]);
    if (!this.isLinkTargetValid(world, target)) {
      target = this.acquireLinkTarget(world);
      pool.aux0[projectile] = target;
    }
    pool.x[projectile] = world.player.x + 12;
    pool.y[projectile] = world.player.y;
    if (target > 0) {
      const enemy = target - 1;
      pool.x2[projectile] = world.enemies.x[enemy];
      pool.y2[projectile] = world.enemies.y[enemy];
    } else {
      pool.x2[projectile] = INTERNAL_WIDTH + 16;
      pool.y2[projectile] = world.player.y;
    }
    if (target < 0) {
      const part = -target - 1;
      pool.x2[projectile] = world.bossParts.x[part];
      pool.y2[projectile] = world.bossParts.y[part];
    }
    return true;
  }

  private isLinkTargetValid(world: GameWorld, target: number): boolean {
    if (target > 0) {
      const enemy = target - 1;
      return world.enemies.indices.active[enemy] !== 0
        && world.enemies.state[enemy] !== EnemyState.Dying
        && world.enemies.x[enemy] >= world.player.x - LINK_TARGET_BEHIND_TOLERANCE;
    }
    if (target < 0) {
      const part = -target - 1;
      return world.bosses.active
        && world.bossParts.indices.active[part] !== 0
        && world.bossParts.visible[part] !== 0
        && world.bossParts.vulnerable[part] !== 0
        && world.bossParts.x[part] >= world.player.x - LINK_TARGET_BEHIND_TOLERANCE;
    }
    return false;
  }
}
